/**
 * Responsibility: a wall of cards, at one size, that does not clip.
 *
 * The grid is padded and the tiles are a fixed width rather than a column
 * count. A scrolling box clips on both axes whatever the CSS says, so the
 * room a tile needs to grow on hover has to be reserved rather than
 * overflowed; and stretching tiles to fill a column count would break the
 * one rule the tile exists to keep, which is that every card is the same
 * size whether or not it is slabbed.
 */
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { CollectionTile } from '../collection/CollectionTile';
import type { ShelfCard } from './shelfCard';

/** Large enough to read the card at a glance. */
export const TILE_WIDTH = 208;

type ShelfGridProps = {
  cards: ShelfCard[];
  onOpen?: (card: ShelfCard) => void;
  /** Per-tile overlay, for a remove or unlike button. */
  corner?: (card: ShelfCard) => ReactNode;
  emptyMessage?: string;
};

export function ShelfGrid({ cards, onOpen, corner, emptyMessage }: ShelfGridProps) {
  if (cards.length === 0) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', flex: 1, minHeight: 200, px: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
          {emptyMessage ?? 'Nothing here yet.'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', p: 1.5 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(auto-fill, minmax(${TILE_WIDTH}px, ${TILE_WIDTH}px))`,
          gap: 2,
          justifyContent: 'start',
          alignContent: 'start',
        }}
      >
        {cards.map((card) => (
          <Stack key={card.key} spacing={0.5} sx={{ position: 'relative' }}>
            <CollectionTile
              card={card}
              width={TILE_WIDTH}
              onOpen={onOpen ? () => onOpen(card) : undefined}
              corner={corner?.(card)}
            />
          </Stack>
        ))}
      </Box>
    </Box>
  );
}
